'use client';

import clsx from 'clsx';

import { Button } from '@virginmediao2/momentum-nextjs/button';
import { FlexBlock } from '@virginmediao2/momentum-nextjs/flex-block';
import { Icon } from '@virginmediao2/momentum-nextjs/icon';
import { type FC, useEffect, useState } from 'react';

import { FarXmark } from '@virginmediao2/momentum-icons/icons/far';

import {
  Section,
  SectionProps,
} from '@/components/momentum/components/section';

import styles from './rpi-section.module.scss';

export type FontSize =
  | 'T200'
  | 'T300'
  | 'T400'
  | 'T500'
  | 'T600'
  | 'T700'
  | 'T800'
  | 'T900';
export type VariantType =
  | 'primary'
  | 'secondary'
  | 'transparent'
  | 'grey'
  | 'blue'
  | 'darkblue'
  | 'lightblue'
  | 'orange'
  | 'violet'
  | 'yellow'
  | 'lightgreen';

export interface RpiProps extends Omit<SectionProps, 'variant' | 'content'> {
  variant: VariantType;
  font_size_mobile?: FontSize;
  font_size_tablet?: FontSize;
  font_size_desktop?: FontSize;
  vertical_rhythm?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  sticky?: boolean;
  id?: string;
  can_dismiss?: boolean;
}
const darkBgs: VariantType[] = ['blue', 'darkblue'];

export const STORAGE_KEY = 'rpi_banner_';

export const RpiSection: FC<RpiProps> = ({
  variant,
  font_size_mobile,
  font_size_tablet,
  font_size_desktop,
  vertical_rhythm,
  sticky,
  can_dismiss,
  children,
  id,
  ...sectionProps
}) => {
  const storageKey = `${STORAGE_KEY}${id}`;
  const [show, setHide] = useState(false);

  useEffect(() => {
    const hasClosed = localStorage.getItem(storageKey);
    if (!hasClosed) {
      setHide(true);
    }
  }, []);

  const bgClasses = clsx(
    variant && styles[`rpi--variant-${variant}`],
    sticky && styles['rpi--sticky']
  );
  const fontClasses = clsx(
    font_size_mobile && styles[`rpi--fontsize-mobile-${font_size_mobile}`],
    font_size_tablet && styles[`rpi--fontsize-tablet-${font_size_tablet}`],
    font_size_desktop && styles[`rpi--fontsize-desktop-${font_size_desktop}`]
  );

  const dismissClass = darkBgs.includes(variant)
    ? clsx(styles[`rpi-dismiss-btn-light`])
    : clsx(styles[`rpi-dismiss-btn-dark`]);

  const dismissBtnVariant = darkBgs.includes(variant) ? 'secondary' : 'primary';

  function handleClose() {
    localStorage.setItem(storageKey, 'true');
    setHide(false);
  }

  if (!show && can_dismiss) {
    return null;
  }

  return (
    <div className={bgClasses} data-testid='rpi-section'>
      <Section
        {...sectionProps}
        variant='transparent'
        verticalRhythm={vertical_rhythm}
        sticky={sticky}
      >
        <FlexBlock>
          <div className={fontClasses}>{children}</div>

          {can_dismiss && (
            <Button
              className={dismissClass}
              data-testid='dismiss-btn'
              decoration='transparent'
              variant={dismissBtnVariant}
              iconOnly
              aria-label='Close'
              onClick={handleClose}
              icon={<Icon icon={FarXmark} size='lg'></Icon>}
            ></Button>
          )}
        </FlexBlock>
      </Section>
    </div>
  );
};
